import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Link,
  Box,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuth } from "../../context/AuthContext";

// Import components
import RegistrationForm from "./components/RegistrationForm";
import RoleSelector from "./components/RoleSelector";
import OnboardingWizard from "./components/OnboardingWizard";
import FeatureHighlights from "./components/FeatureHighlights";
const headerLogo = "/assets/images/teamscout-logo.png";

// Styled components
const AuthContainer = styled(Container)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
}));

const AuthPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  overflow: "hidden",
  boxShadow: theme.shadows[10],
}));

const FormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
}));

const FeatureSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  textAlign: "center",
}));

const EnhancedRegister = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { register, error, clearError, isAuthenticated } = useAuth();

  // State
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "",
    school: "",
    department: "",
  });
  const [selectedRole, setSelectedRole] = useState("");

  // Steps for registration process
  const steps = ["Select Role", "Create Account", "Complete Profile"];

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  // Handle role selection
  const handleRoleSelect = (role, proceed = false) => {
    setSelectedRole(role);
    setRegistrationData((prev) => ({
      ...prev,
      role,
    }));

    if (proceed) {
      setStep(1);
    }
  };

  // Handle registration form submission
  const handleRegister = async (formData) => {
    setLoading(true);

    // Update registration data
    const updatedData = {
      ...registrationData,
      ...formData,
    };

    setRegistrationData(updatedData);

    try {
      await register(updatedData);
      setStep(2);
    } catch (err) {
      console.error("Registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle onboarding completion
  const handleOnboardingComplete = async (profileData) => {
    setLoading(true);

    try {
      // In a real app, you would update the user profile with this data
      console.log("Onboarding complete with data:", profileData);

      // Redirect to dashboard
      navigate("/dashboard");
    } catch (err) {
      console.error("Onboarding error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Render content based on step
  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Choose Your Role
            </Typography>
            <RoleSelector
              selectedRole={selectedRole}
              onRoleSelect={handleRoleSelect}
            />
          </>
        );
      case 1:
        return (
          <>
            <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
              <IconButton onClick={() => setStep(0)} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">Create Your Account</Typography>
            </Box>
            <RegistrationForm
              onSubmit={handleRegister}
              loading={loading}
              error={error}
              clearError={clearError}
            />
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="body2">
                Already have an account?{" "}
                <Link
                  component={RouterLink}
                  to="/login"
                  variant="body2"
                  fontWeight="bold"
                >
                  Sign In
                </Link>
              </Typography>
            </Box>
          </>
        );
      case 2:
        return (
          <OnboardingWizard
            onComplete={handleOnboardingComplete}
            loading={loading}
            error={error}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AuthContainer maxWidth="lg">
      <AuthPaper elevation={10}>
        <Grid container>
          <Grid item xs={12} md={6}>
            <FormSection>
              <LogoContainer>
                <img src={headerLogo} alt="TeamScout" style={{ height: 60 }} />
              </LogoContainer>

              {step < 2 && (
                <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}

              {renderStepContent()}
            </FormSection>
          </Grid>

          <Grid item md={6}>
            <FeatureSection>
              <FeatureHighlights
                role={selectedRole || "coach"}
                onGetStarted={() => {}}
              />
            </FeatureSection>
          </Grid>
        </Grid>
      </AuthPaper>
    </AuthContainer>
  );
};

export default EnhancedRegister;
